// Registreringslenker — lesing, gyldighetsnavn, skjemakontroll og tekster.
//
// HVOR DET AVGJØRES HVA SOM ER SANT OM EN LENKE:
//  · Om lenken KAN BRUKES avgjøres av tjenesten. Den kaller `kontrollerLenke()`
//    i @skade/dugnad og legger svaret i `gyldig` + `grunn` på hver rad fra
//    `GET /lenker`. De brukes ORDRETT her. Klienten regner ikke om — to
//    gyldighetsregler er den divergensen VALE §3 finnes for å hindre.
//  · Hva som er GRUNNEN navngir vi lokalt, for å kunne sette en merkelapp
//    («Tilbakekalt», «Utløpt», «Brukt opp»). Rekkefølgen er tjenestens:
//    tilbakekall først, deretter utløp, deretter bruksantall.
//  · Sier tjenesten ingenting om gyldighet (eldre utrulling), regnes den lokalt
//    — men da er det et FALLBACK, ikke normalveien, og det står i koden under.
//
// KODEN SELV FINNES ALDRI HER. `lenke.id` returneres i klartekst kun i svaret på
// `POST /lenker`; lageret har bare hash + oppslagsprefiks. Lista bærer derfor
// `idPrefiks` (6 tegn) — nok til å kjenne igjen og tilbakekalle en lenke, ikke
// nok til å bruke den. Alt i denne fila som heter «prefiks» handler om det, og
// ingenting her kan rekonstruere en lenke som er mistet.
import { LENKE_LEVETID_MS } from '@skade/dugnad/prosjekt';
import type { LenkeKropp } from '../api/typer.js';
import { registreringsLenke } from './rute.js';

const TIME_MS = 60 * 60 * 1000;
export const DØGN_MS = 24 * TIME_MS;

/**
 * Grensene `NyLenkeSchema`/`RegistreringslenkeSchema` håndhever i tjenesten.
 *
 * Skrevet av her for å kunne si fra FØR kallet, på samme måte som
 * registreringsskjemaet gjentar ansattnummer-regelen. Tjenesten er fortsatt den
 * som avgjør — dette er tidlig svar, ikke en andre sannhet.
 */
export const MAKS_BRUK = 200;
export const MAKS_LEVETID_DØGN = 90;
export const MAKS_TILHØRIGHET = 120;
export const MAKS_MERKNAD = 200;

/** Standard levetid, hentet fra domenepakken så tallet bare finnes ett sted. */
export const STANDARD_LEVETID_DØGN = Math.round(LENKE_LEVETID_MS / DØGN_MS);

// ── Lesing av `GET /lenker` ────────────────────────────────────────────────

/**
 * Én rad fra `GET /lenker`. Merk at `id` IKKE er med — se filhodet.
 */
export interface LenkeRad {
  /** Første 6 tegn av oppslagsdelen. Identifiserer raden for tilbakekall. */
  idPrefiks: string;
  /** Pseudonymet som laget lenken («admin» når drift brukte admin-token). */
  opprettetAv: string;
  opprettetTsMs: number;
  utloperTsMs: number;
  maksBruk: number;
  brukt: number;
  tilhorighet?: string;
  merknad?: string;
  tilbakekaltTsMs: number | null;
  /** Tjenestens dom. `null` betyr at svaret ikke inneholdt feltet. */
  gyldig: boolean | null;
  /** Tjenestens egen forklaring, når den er med. */
  grunn?: string;
}

function objekt(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function tekst(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v : undefined;
}

function heltall(v: unknown, standard: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : standard;
}

/**
 * Les én rad. Uten `idPrefiks` returneres null: en rad vi ikke kan identifisere
 * kan heller ikke tilbakekalles, og å vise den ville gitt en knapp som ikke
 * virker.
 */
export function lesLenke(rå: unknown): LenkeRad | null {
  const r = objekt(rå);
  if (!r) return null;
  const idPrefiks = tekst(r.idPrefiks);
  if (!idPrefiks) return null;
  const tilbakekaltTsMs = typeof r.tilbakekaltTsMs === 'number' && Number.isFinite(r.tilbakekaltTsMs)
    ? r.tilbakekaltTsMs
    : null;
  return {
    idPrefiks,
    opprettetAv: tekst(r.opprettetAv) ?? '(ukjent)',
    opprettetTsMs: heltall(r.opprettetTsMs, 0),
    utloperTsMs: heltall(r.utloperTsMs, 0),
    maksBruk: Math.max(1, heltall(r.maksBruk, 1)),
    brukt: Math.max(0, heltall(r.brukt, 0)),
    tilhorighet: tekst(r.tilhorighet),
    merknad: tekst(r.merknad),
    tilbakekaltTsMs,
    gyldig: typeof r.gyldig === 'boolean' ? r.gyldig : null,
    grunn: tekst(r.grunn),
  };
}

/** `{ lenker: [...] }` → rader, nyeste først. */
export function lesLenker(rå: unknown): LenkeRad[] {
  const liste = objekt(rå)?.lenker;
  if (!Array.isArray(liste)) return [];
  return liste
    .map(lesLenke)
    .filter((l): l is LenkeRad => l !== null)
    .sort((a, b) => b.opprettetTsMs - a.opprettetTsMs
      || a.idPrefiks.localeCompare(b.idPrefiks, 'nb'));
}

// ── Gyldighet ──────────────────────────────────────────────────────────────

export type Lenketilstand = 'gyldig' | 'tilbakekalt' | 'utløpt' | 'oppbrukt' | 'ugyldig';

/** Merkelappen tilstanden får i lista. */
export const TILSTAND_MERKE: Record<Lenketilstand, string> = {
  gyldig: 'Gyldig',
  tilbakekalt: 'Tilbakekalt',
  utløpt: 'Utløpt',
  oppbrukt: 'Brukt opp',
  ugyldig: 'Kan ikke brukes',
};

/** Merke-variant fra style.css. Aldri farge alene — teksten står ved siden av. */
export const TILSTAND_VARIANT: Record<Lenketilstand, string> = {
  gyldig: 'sage',
  tilbakekalt: 'fare',
  utløpt: '',
  oppbrukt: '',
  ugyldig: 'fare',
};

export interface LenkeVurdering {
  tilstand: Lenketilstand;
  gyldig: boolean;
  /** Tjenestens egen setning, når den fulgte med. */
  grunn?: string;
}

/** Hvilken av de tre kjente grunnene passer? `null` = ingen av dem. */
function grunnNavn(rad: LenkeRad, naaMs: number): Exclude<Lenketilstand, 'gyldig' | 'ugyldig'> | null {
  if (rad.tilbakekaltTsMs !== null) return 'tilbakekalt';
  if (naaMs > rad.utloperTsMs) return 'utløpt';
  if (rad.brukt >= rad.maksBruk) return 'oppbrukt';
  return null;
}

/**
 * Navnet på tilstanden en rad er i.
 *
 * Tjenestens `gyldig` VINNER over vår lokale regning. Klokka i nettleseren kan
 * være minutter feil, og det er tjenesten som til slutt godtar eller avviser
 * registreringen — en merkelapp som sier «Utløpt» om en lenke tjenesten
 * fortsatt tar imot, ville sendt operatøren til å lage en lenke hun ikke trengte.
 */
export function lenketilstand(rad: LenkeRad, naaMs: number): LenkeVurdering {
  const navn = grunnNavn(rad, naaMs);
  const grunn = rad.grunn ? { grunn: rad.grunn } : {};
  if (rad.gyldig === true) return { tilstand: 'gyldig', gyldig: true };
  if (rad.gyldig === false) return { tilstand: navn ?? 'ugyldig', gyldig: false, ...grunn };
  // Fallback: svaret manglet `gyldig`. Da er lokal regning bedre enn å påstå
  // «Gyldig» om en lenke som åpenbart er tilbakekalt eller utløpt.
  if (navn) return { tilstand: navn, gyldig: false, ...grunn };
  return { tilstand: 'gyldig', gyldig: true };
}

// ── Tekster ────────────────────────────────────────────────────────────────

/** «brukt 1 av 3». Alltid begge tallene — «brukt 1» sier ikke om det er slutt. */
export function bruktTekst(brukt: number, maksBruk: number): string {
  const m = Math.max(1, Number.isFinite(maksBruk) ? Math.trunc(maksBruk) : 1);
  const b = Math.min(m, Math.max(0, Number.isFinite(brukt) ? Math.trunc(brukt) : 0));
  return `brukt ${b} av ${m}`;
}

/** Hva slags lenke bruksantallet gjør den til. */
export function slagTekst(maksBruk: number): string {
  const m = Math.max(1, Number.isFinite(maksBruk) ? Math.trunc(maksBruk) : 1);
  return m === 1 ? 'Personlig invitasjon' : `Områdelenke — inntil ${m} personer`;
}

/**
 * Prefikset som det vises i lista: «a1b2c3…».
 *
 * Ellipsen er ikke pynt. Den sier at dette er en DEL av koden, så ingen prøver
 * å dele de seks tegnene i den tro at de er lenken.
 */
export function prefiksVisning(idPrefiks: string): string {
  const p = (idPrefiks ?? '').trim();
  return p ? `${p}…` : '(ukjent)';
}

/** «utløper om 6 døgn» / «utløper om 5 timer» / «utløp for 2 døgn siden». */
export function utløpstekst(utloperTsMs: number, naaMs: number): string {
  const diff = utloperTsMs - naaMs;
  if (!Number.isFinite(diff)) return 'ukjent utløp';
  if (diff <= 0) {
    const timer = Math.floor(-diff / TIME_MS);
    if (timer < 24) return 'utløpt';
    const døgn = Math.floor(timer / 24);
    return døgn === 1 ? 'utløp for ett døgn siden' : `utløp for ${døgn} døgn siden`;
  }
  const timer = Math.ceil(diff / TIME_MS);
  if (timer <= 1) return 'utløper innen en time';
  if (timer < 24) return `utløper om ${timer} timer`;
  const døgn = Math.round(diff / DØGN_MS);
  return døgn === 1 ? 'utløper om ett døgn' : `utløper om ${døgn} døgn`;
}

// ── Skjemaet ───────────────────────────────────────────────────────────────

/** Levetider å velge mellom. `verdi` er antall døgn, som streng (`<select>`). */
export const LEVETIDSVALG: readonly { verdi: string; navn: string }[] = [
  { verdi: '1', navn: '1 døgn' },
  { verdi: '3', navn: '3 døgn' },
  { verdi: String(STANDARD_LEVETID_DØGN), navn: `${STANDARD_LEVETID_DØGN} døgn (standard)` },
  { verdi: '14', navn: '14 døgn' },
  { verdi: '30', navn: '30 døgn' },
  { verdi: String(MAKS_LEVETID_DØGN), navn: `${MAKS_LEVETID_DØGN} døgn (lengst mulig)` },
];

export interface LenkeUtkast {
  maksBruk: number;
  tilhorighet: string;
  merknad: string;
  levetidMs: number;
}

/** Standardverdiene skjemaet åpner med: det vanlige tilfellet er ett klikk. */
export function standardUtkast(): LenkeUtkast {
  return { maksBruk: 1, tilhorighet: '', merknad: '', levetidMs: LENKE_LEVETID_MS };
}

/** Antall døgn → millisekunder. Ugyldig inndata faller til standard levetid. */
export function levetidMsAv(døgn: string): number {
  const n = Number(døgn);
  return Number.isFinite(n) && n > 0 ? Math.round(n * DØGN_MS) : LENKE_LEVETID_MS;
}

/**
 * Kontroll før kallet. Speiler `NyLenkeSchema` i tjenesten (`maksBruk` 1–200,
 * `tilhorighet` ≤ 120, `merknad` ≤ 200, `levetidMs` 1 s – 90 døgn).
 */
export function validerNyLenke(u: LenkeUtkast): { felt: string; grunn: string } | null {
  if (!Number.isFinite(u.maksBruk) || !Number.isInteger(u.maksBruk)) {
    return { felt: 'maksBruk', grunn: 'antall bruk må være et helt tall' };
  }
  if (u.maksBruk < 1) return { felt: 'maksBruk', grunn: 'antall bruk må være minst 1' };
  if (u.maksBruk > MAKS_BRUK) {
    return { felt: 'maksBruk', grunn: `antall bruk kan være høyst ${MAKS_BRUK}` };
  }
  if (u.tilhorighet.length > MAKS_TILHØRIGHET) {
    return { felt: 'tilhorighet', grunn: `tilhørighet kan være høyst ${MAKS_TILHØRIGHET} tegn` };
  }
  if (u.merknad.length > MAKS_MERKNAD) {
    return { felt: 'merknad', grunn: `merknaden kan være høyst ${MAKS_MERKNAD} tegn` };
  }
  if (!Number.isFinite(u.levetidMs) || u.levetidMs < 1000) {
    return { felt: 'levetid', grunn: 'levetiden må være minst ett sekund' };
  }
  if (u.levetidMs > MAKS_LEVETID_DØGN * DØGN_MS) {
    return { felt: 'levetid', grunn: `levetiden kan være høyst ${MAKS_LEVETID_DØGN} døgn` };
  }
  return null;
}

/**
 * Utkastet → kroppen `POST /lenker` skal ha.
 *
 * Tomme valgfrie felt UTELATES framfor å sendes som tom streng: tjenesten
 * lagrer `tilhorighet: ''` som et satt felt, og da ville registreringssiden vist
 * en tom «Tilhørighet fra lenken»-rad til den som melder seg.
 */
export function lenkeKropp(u: LenkeUtkast): LenkeKropp {
  const t = u.tilhorighet.trim();
  const m = u.merknad.trim();
  return {
    maksBruk: u.maksBruk,
    levetidMs: u.levetidMs,
    ...(t ? { tilhorighet: t } : {}),
    ...(m ? { merknad: m } : {}),
  };
}

// ── Svaret på `POST /lenker` (engangsvisningen) ─────────────────────────────

export interface NyLenke {
  /** Hele koden. Finnes bare i dette svaret, aldri senere. */
  id: string;
  idPrefiks: string;
  utloperTsMs: number;
  maksBruk: number;
  tilhorighet?: string;
  merknad?: string;
  /** Full URL. Kommer fra tjenestens `url` når den er med. */
  url: string;
  /**
   * Falsk når `url` manglet i svaret og adressen er satt sammen av nettleserens
   * eget opphav. Skjermen SKAL si det da — en lenke bygget på feil vert virker
   * ikke, og det er verdt å vite før den deles ut.
   */
  urlFraTjenesten: boolean;
}

/**
 * Les 201-svaret. Uten `lenke.id` finnes det ingenting å vise, og da er null
 * riktigere enn et kort med tom kode: koden kan ikke hentes fram igjen, så en
 * halv visning ville vært en tapt lenke som ser ut som en vellykket.
 */
export function lesNyLenke(rå: unknown, opphav: string): NyLenke | null {
  const r = objekt(rå);
  const l = objekt(r?.lenke);
  const id = tekst(l?.id);
  if (!id) return null;
  const fraTjenesten = tekst(r?.url);
  return {
    id,
    // Prefikset regnes av koden når tjenesten ikke sendte det: de seks første
    // tegnene er per definisjon de samme (`idPrefiks()` i server.ts).
    idPrefiks: tekst(l?.idPrefiks) ?? id.slice(0, 6),
    utloperTsMs: heltall(l?.utloperTsMs, 0),
    maksBruk: Math.max(1, heltall(l?.maksBruk, 1)),
    tilhorighet: tekst(l?.tilhorighet),
    merknad: tekst(l?.merknad),
    url: fraTjenesten ?? registreringsLenke(opphav, id),
    urlFraTjenesten: fraTjenesten !== undefined,
  };
}

/**
 * Peker lenken på et annet opphav enn appen kjører på?
 *
 * Da har drift satt `DUGNAD_BASIS_URL` til noe annet enn denne verten, og hver
 * utstedt lenke går et sted operatøren ikke ser. Det er ikke nødvendigvis feil
 * (én app kan ligge bak flere navn), men det skal stå i UI-et framfor å oppdages
 * av den som ikke kommer inn.
 */
export function annetOpphav(url: string, opphav: string): boolean {
  try {
    return new URL(url).origin !== new URL(opphav).origin;
  } catch {
    // Kan vi ikke lese adressene, har vi ingenting å påstå.
    return false;
  }
}
